import Vue from 'vue'
import { Dialog, Loading, Notify } from 'quasar'
import store from '@/store'
import router from '@/router'
import { USER_LOGOUT } from '@/store/modules/user/action-types'
import { APP_MODE_PLATFORM, MODAL_EVENT_UPDATE_SERVICE_EVENT } from '@/consts/app'
import { checkDeviceRegistration, unregister } from '@/utils/notifications'
import { basculerService } from '@/utils/services'
import { devLog } from '@/utils/index'
import { dateFormatFrLong } from '@/utils/data/date'
import { fetchAllEvents } from '@/utils/api/agency'
import { deleteAgencyEvent, getAgencyEvent } from '@/utils/api/event'
import { getAppVersion } from '@/utils/api/utils'
import DialogAppVersion from 'components/Dialog/DialogAppVersion'
import { ROUTE_NAME_ADMINISTRATOR, ROUTE_NAME_LINKS, ROUTE_NAME_PARAMS } from '@/router/routes'
import invisibleVueInstanceMixin from '@/mixins/invisibleVueInstanceMixin'

const STORAGE_KEY = 'LWF-UI'
const UI_DEFAULT_STATE = {
  leftDrawerOpen: false,
  leftDrawerMini: false,
  leftDrawerNav: APP_MODE_PLATFORM,
  rightDrawerOpen: false,
  modals: [],
  unreadEvents: 0,
  dark: false,
  notificationStatus: true,
  notificationError: null,
  logs: [],
  serverVersion: 'Chargement...',
  serverMaintenance: {
    maintenance: false,
    message: null
  },
  ptiModalPermissions: true,
  ptiModalVisible: false,
  serviceEvents: {
    focusedAgent: null,
    loading: false,
    filters: {},
    events: []
  },
  panels: {
    [ROUTE_NAME_LINKS]: false,
    [ROUTE_NAME_ADMINISTRATOR]: false,
    [ROUTE_NAME_PARAMS]: false
  }
}
let modalIndex = 0
const LOG_LEVELS = [
  { lvl: 'log', color: 'primary' },
  { lvl: 'warning', color: 'warning' },
  { lvl: 'error', color: 'negative' },
  { lvl: 'info', color: 'info' }
]
/**
 * Instance de gestion de l'état de l'ui
 * @global
 */
export const $ui = new Vue({
  name: 'Ui',
  mixins: [invisibleVueInstanceMixin],
  data () {
    return _.merge({}, UI_DEFAULT_STATE, JSON.parse(window.localStorage.getItem(STORAGE_KEY)))
  },
  computed: {
    isDev () {
      return process.env.API !== process.env.API_PROD
    },
    /**
     * Calcule la couleur idéale pour le fond d'un élements
     * @returns {string}
     */
    bgColor () {
      return `bg-${this.dark ? 'dark' : 'white'}`
    },
    /**
     * Calcule la couleur idéale pour le texte d'un élement
     * @returns {string}
     */
    textColor () {
      return `text-${this.dark ? 'white' : 'primary'}`
    },

    /**
     * Calcule la couleur idéale pour le texte d'un élement
     * @returns {string}
     */
    textDarkColor () {
      return `text-${this.dark ? 'primary' : 'white'}`
    },
    isPlatform () {
      return this.leftDrawerNav === APP_MODE_PLATFORM
    },
    mappedServiceEvents () {
      const { serviceEvents: { focusedAgent, events } } = this
      return events.map((event) => ({
        ...event,
        _event: event,
        hover: event.hover || (focusedAgent && event.user && event.user.id === focusedAgent)
      }))
    }
  },
  watch: {
    $data: {
      deep: true,
      handler ({ modals, unreadEvents, logs, serviceEvents, ...ui }) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ui))
      }
    },
    dark: {
      immediate: true,
      handler (v) {
        this.$q.dark.set(v)
      }
    },
    notificationStatus: {
      immediate: true,
      async handler (v) {
        if (store && store.getters['user/isLoggedIn']) {
          if (v) {
            return this.fbCheckDeviceRegistration()
          } else {
            try {
              await this.fbUnregister()
            } catch (e) {}
          }
        }
      }
    }
  },
  created () {
    if (this.isDev) {
      window.showLogs = true
      if (window.CAPTURE_LOG) {
        devLog('Capture de la console activé')
        LOG_LEVELS.forEach(({ lvl, color }) => {
          const f = console[lvl]
          console[lvl] = (...args) => {
            this.logs.push({
              logLevel: lvl,
              color,
              date: dateFormatFrLong(new Date()),
              args
            })
            f(...args)
          }
        })
      }
    }
  },
  methods: {
    async fbCheckDeviceRegistration () {
      const { status, error = { code: null } } = await checkDeviceRegistration()
      this.notificationStatus = status
      switch (error.code) {
        case null:
          this.notificationError = null
          break
        case 'messaging/permission-blocked':
          this.notificationError = `Vous devez autoriser les notifications afin de recevoir les alertes ${process.env.APP.name} en temps réel`
          break
        default:
          this.notificationError = `Erreur inconnue : ${error.code}`
      }
    },
    async fbUnregister () {
      try {
        await unregister()
      } catch (e) {
        devLog('Token unregistration failed', e)
      }
    },
    eventsRefresh (data = []) {
      const newEvents = [...this.serviceEvents.events]
      data.forEach((_s) => {
        const i = newEvents.findIndex((s) => s.id === _s.id)
        if (i === -1) {
          newEvents.push(_s)
        } else {
          newEvents.splice(i, 1, _s)
        }
      })
      _.set(this.serviceEvents, 'events', _.sortBy(newEvents, 'createdAt').reverse())
    },
    _eventDelete (_event) {
      return loadingHandler(async () => {
        const { event } = await deleteAgencyEvent(_event)
        this.$emit(MODAL_EVENT_UPDATE_SERVICE_EVENT, [{
          ...event,
          _deleted: true
        }])
        const { events = [] } = this.serviceEvents
        const i = events.findIndex((e) => _event.id === e.id)
        if (i !== -1) {
          events.splice(i, 1)
        }
      })
    },
    eventDelete (_event) {
      dialogConfirm('La supression est définitive')
        .onOk(() => this._eventDelete(_event))
    },
    async getServiceEvent ({ reference }) {
      const { event } = await getAgencyEvent(reference)
      this.eventsRefresh([event])
    },
    async fetchFilteredEvents () {
      this.serviceEvents.loading = true
      try {
        this.serviceEvents.events = []
        const r = await fetchAllEvents({ filters: this.serviceEvents.filters })
        this.serviceEvents.events = r.data
      } catch (e) {
        devLog('Failed', e)
      }
      this.serviceEvents.loading = false
    },
    /**
     * Ajoute une modal
     * @param {{ component: { is: *, props?: {}, on?: Object.<string, function> }}} opt
     * @param opt
     * @returns {{hide: Function}}
     */
    addModal (opt) {
      if (!opt.component) throw new Error('Un composant est requis')
      const context = {
        open: true,
        props: {
          persistent: true
        },
        ...opt,
        on: {
          hide: () => {
            setTimeout(() => {
              this.modals.splice(index - 1, 1)
            }, 250)
          }
        },
        id: modalIndex++
      }
      const index = this.modals.push(context)
      const _on = {}
      return {
        hide: () => { context.open = false },
        setValue: (subject) => _.set(context, ['component', 'props', 'value'], subject),
        on: (k, f) => {
          if (_on[k]) {
            _on[k].push(f)
          } else {
            _on[k] = [f]
            this.$set(context.component.on, k, (...args) => _on[k].forEach((_f) => _f(...args)))
          }
        }
      }
    },
    toggleRightDrawerOpen () {
      this.unreadEvents = 0
      this.rightDrawerOpen = !this.rightDrawerOpen
    }
  }
})

/**
 * Parse le code de version de avoir une valeur numérique comparables
 * @param code
 * @return {string}
 * @private
 */
const _parseVersionCode = (code) => code.split('.').map((s) => s.padStart(2, '0')).join('')

/**
 * Vérifie la version du logiciel et invite à télécharger la plus récente
 * @return {Promise<void>}
 */
export const checkAppVersion = async () => {
  const { version: { code }, maintenance, ptiModalPermissions } = await getAppVersion()
  Object.assign(
    $ui,
    {
      serverVersion: code,
      serverMaintenance: maintenance,
      ptiModalPermissions
    })
  if (_parseVersionCode(code) > _parseVersionCode(process.env.APP_VERSION)) {
    devLog('Nouvelle version dispo : ' + code)
    Dialog.create({
      component: DialogAppVersion
    })
  }
}

/**
 * Confirmation par défaut
 * @param opt
 * @returns {DialogChainObject}
 */
export const dialogConfirm = (opt = {}) => {
  const _opt = typeof opt === 'string'
    ? { message: opt }
    : opt
  return Dialog.create({
    persistent: true,
    cancel: true,
    title: 'Confirmation',
    ..._opt
  })
}

export const CONFIRM_REJECT_CANCEL = new Error('cancel')
export const CONFIRM_REJECT_DISMISS = new Error('dismiss')

/**
 * Cofirmation sous forme de promise
 * @param opt
 * @returns {Promise<unknown>}
 */
export const promiseConfirm = (opt) => new Promise((resolve, reject) => {
  dialogConfirm(opt)
    .onOk(resolve)
    .onCancel(() => reject(CONFIRM_REJECT_CANCEL))
    .onDismiss(() => reject(CONFIRM_REJECT_DISMISS))
})

/**
 * Handler pour exclure les exceptions du cancel / dismiss
 * @param f
 * @returns {Promise<void>}
 */
export const tryCatchConfirm = async (f) => {
  try {
    return await f()
  } catch (e) {
    if ([CONFIRM_REJECT_CANCEL, CONFIRM_REJECT_DISMISS].indexOf(e) === -1) {
      return Promise.reject(e)
    }
  }
}

/**
 * Raccourcis pour les notify
 * @type {{
 * positive: function (opt: {}),
 * negative: function (opt: {}),
 * warning: function (opt: {}),
 * info: function (opt: {}),
 * }}
 */
export const toast = {}
;[
  'positive',
  'negative',
  'warning',
  'info'
].forEach((color) => {
  toast[color] = (opt = {}) => {
    const _opt = typeof opt === 'string'
      ? { message: opt }
      : opt
    return Notify.create({
      color,
      ..._opt
    })
  }
})

/**
 * Handler générique pour les traitements long
 * - Affiche un loader
 * - Lance la callback
 * - Masque le loader
 * @param f
 * @param {{message?: string, reject?: boolean}} opt
 *  - reject pour récupérer l'erreur
 * @returns {Promise<void>}
 */
export const loadingHandler = async (f, opt = { message: 'Chargement' }) => {
  Loading.show({
    message: 'Chargement',
    ...opt
  })
  try {
    return await f()
  } catch (e) {
    console.error(e)
    toast.negative({
      message: 'Une erreur est survenue',
      caption: typeof e === 'string'
        ? e
        : e.message
    })
    return opt.reject
      ? Promise.reject(e)
      : undefined
  } finally {
    Loading.hide()
  }
}

/**
 * Gère la déconnection de l'utilisateur
 */
export const logout = async (force = false) => {
  const serviceActif = store.getters['service/serviceActif']
  try {
    if (!force && serviceActif) {
      await basculerService(serviceActif)
    }
    await loadingHandler(async () => {
      await $ui.fbUnregister()
      await store.dispatch('user/' + USER_LOGOUT)
    })
    await router.push('/')
  } catch (e) {
    devLog(e)
  }
}

/**
 * Map les filtres automatique de type select pour remplacer les slot selection par un compteur
 * @param type
 * @return {{counter: boolean, type: *, props: {'use-chips': boolean}}}
 */
export const mapSelectFilters = (type) => ({
  type,
  counter: true,
  props: {
    'use-chips': false
  }
})
